import React, { useEffect, useMemo, useRef } from "react";

import { isValidPostCode, useAddressValidation, useGetRegionsByCountry, useTranslation } from "utils";

import { COUNTRY_CODE } from "consts";

import { Address } from "models";

import { Col, Form, Input, OptGroup, Option, Row, Select } from "components";

export const AddressForm = ({ dataTestId, address, countryCode }: { dataTestId?: string; address: Address | null; countryCode: COUNTRY_CODE }) => {
  const { t } = useTranslation(["onboarding", "enums"]);

  const { isCityValid } = useAddressValidation({ country: countryCode, t });

  const form = Form.useFormInstance();

  const { data: regionsData = { isGrouped: false, regions: [] } } = useGetRegionsByCountry({ countryCode });

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    form.setFieldValue("region", undefined);
  }, [countryCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const regionOptions = useMemo(() => {
    if (regionsData.isGrouped) {
      return regionsData.groupedRegions.map((group) => (
        <OptGroup key={`group-${group.country}`} label={group.country}>
          {group.regions.map((region, regionIndex) => (
            <Option
              data-testid={`${dataTestId}-region-select-option-${group.country.toLowerCase().replaceAll(/\s+/g, "-")}-${regionIndex}`}
              value={region}
              key={`${group.country}-${region}`}
            >
              {region}
            </Option>
          ))}
        </OptGroup>
      ));
    }

    return regionsData.regions.map((item) => (
      <Option data-testid={`${dataTestId}-region-select-option-${item}`} value={item} key={item}>
        {item}
      </Option>
    ));
  }, [regionsData, dataTestId]);

  return (
    <Form.Item data-testid={`${dataTestId}-address-form`} name="address" style={{ width: "inherit" }}>
      <Row className="margin-0">
        <Col sm={14} md={16}>
          <Input
            dataTestId={`${dataTestId}-address-street-input`}
            name="addressStreet"
            label={t("farmDetails.addressStreet")}
            rules={[{ required: true, message: t("farmDetails.errorMessage.streetMissing") }]}
            initialValue={address?.addressStreet ?? undefined}
          />
        </Col>
        <Col sm={10} md={8}>
          <Input
            dataTestId={`${dataTestId}-address-number-input`}
            name="addressNumber"
            label={t("farmDetails.addressNumber")}
            rules={[{ required: true, message: t("farmDetails.errorMessage.streetNumberMissing") }]}
            initialValue={address?.addressNumber ?? undefined}
          />
        </Col>
      </Row>
      <Row className="margin-0">
        <Col sm={12} md={12}>
          <Input
            dataTestId={`${dataTestId}-address-city-input`}
            name="addressCity"
            label={t("farmDetails.addressCity")}
            validateTrigger="onBlur"
            rules={[
              { required: true, message: t("farmDetails.errorMessage.cityMissing") },
              {
                validator: async (_: unknown, value: string) => {
                  if (await isCityValid(value, countryCode)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("farmDetails.errorMessage.cityInvalid")));
                },
              },
            ]}
            initialValue={address?.addressCity ?? undefined}
          />
        </Col>
        <Col sm={12} md={12}>
          <Input
            dataTestId={`${dataTestId}-address-zip-input`}
            name="zip"
            initialValue={address?.zip ?? undefined}
            label={t("farmDetails.zip")}
            validateTrigger="onBlur"
            rules={[
              { required: true, message: t("farmDetails.errorMessage.zipMissing") },
              {
                validator: (_: unknown, value: string) => {
                  if (isValidPostCode(value, countryCode)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("farmDetails.errorMessage.zip")));
                },
              },
            ]}
          />
        </Col>
      </Row>
      <Row className="margin-0">
        <Col sm={24} md={24}>
          {/* @ts-expect-error need to convert to ts */}
          <Select
            dataTestId={`${dataTestId}-address-region-select`}
            name="region"
            label={t("farmDetails.region")}
            showSearch
            optionFilterProp="children"
            // @ts-expect-error need to convert to ts
            filterOption={(input, option) => (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())}
            popupMatchSelectWidth={false}
            rules={[{ required: true, message: t("farmDetails.errorMessage.regionMissing") }]}
            initialValue={address?.region}
          >
            {regionOptions}
          </Select>
        </Col>
      </Row>
    </Form.Item>
  );
};
